import React, { useEffect, useState } from 'react';
import { FiEdit2, FiEye } from 'react-icons/fi';
import { MdBusinessCenter } from 'react-icons/md';
import { TableSkeleton } from './TableSkeleton';

interface TableColumn {
  key: string;
  header: string;
  icon?: React.ReactNode;
  renderCell?: (value: any, row: any) => React.ReactNode;
  priority?: number; // Higher number = higher priority (show on smaller screens)
  minWidth?: string; // Add minimum width option for column
  maxWidth?: string; // Add maximum width option for column
  truncate?: boolean; // Add option to truncate content
  width?: string; // Add width option for column
  headerClassName?: string; // Add class for header styling
  cellClassName?: string; // Add class for cell styling
  alwaysVisible?: boolean; // Force column to always be visible regardless of screen size
}

interface TableProps {
  data: any[];
  columns: TableColumn[];
  onRowClick?: (row: any) => void;
  statusColors?: { [key: string]: string };
  statusIcons?: { [key: string]: React.ReactNode };
  title?: string;
  itemsPerPageOptions?: number[];
  defaultItemsPerPage?: number;
  totalItems?: number;
  currentPage?: number;
  totalPages?: number; // Add totalPages prop
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (size: number) => void;
  isLoading?: boolean;
  isSearching?: boolean;
  emptyText?: string;
  className?: string;
  theadClassName?: string;
  thClassName?: string;
  tdClassName?: string;
}

// Add text truncation helper function
const truncateString = (str: string, maxLength: number = 30): string => {
  if (!str) return '';
  return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
};

// Add a function to highlight matching text for search
const highlightMatch = (text: string, searchTerm: string): JSX.Element | string => {
  if (!text) return '';
  if (!searchTerm || searchTerm.trim() === '') return text;

  try {
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = String(text).split(regex);

    if (parts.length <= 1) return text;

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </>
    );
  } catch (e) {
    // In case of any errors with the regex, return the original text
    return text;
  }
};

const ResponsiveTable: React.FC<TableProps> = ({
  data,
  columns,
  onRowClick,
  statusColors = {},
  statusIcons = {},
  title,
  itemsPerPageOptions = [10, 15, 20, 50],
  defaultItemsPerPage = 10,
  totalItems = 0,
  currentPage = 1,
  totalPages: providedTotalPages, // Rename to avoid conflicts
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  isSearching = false,
  emptyText = 'No data available',
  className = '',
  theadClassName = 'bg-red-50 rounded-t-lg overflow-hidden',
  thClassName = 'px-4 py-3 text-left text-sm font-bold text-red-500 uppercase tracking-wider',
  tdClassName = 'px-4 py-4 text-sm text-left',
}) => {
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setScreenSize('mobile');
      } else if (width < 1024) {
        setScreenSize('tablet');
      } else {
        setScreenSize('desktop');
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add a helper function to format status text
  const formatStatus = (status: string): string => {
    if (!status) return '';

    // Replace underscores with spaces
    let formattedStatus = status.replace(/_/g, ' ');

    // Convert to title case (capitalize first letter of each word)
    formattedStatus = formattedStatus
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle special cases
    switch (status.toUpperCase()) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PAYMENT_RECEIVED':
        return 'Payment Received';
      default:
        return formattedStatus;
    }
  };

  const getStatusClass = (status: string) => {
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    return statusIcons[status] || null;
  };

  // Sort columns based on priority for tablet view
  const getPriorityColumns = () => {
    const alwaysVisibleColumns = columns.filter(col => col.alwaysVisible);
    const prioritizedColumns = [...columns]
      .filter(col => !col.alwaysVisible)
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))
      .slice(0, screenSize === 'tablet' ? 4 - alwaysVisibleColumns.length : columns.length);

    return [...prioritizedColumns, ...alwaysVisibleColumns];
  };

  // Helper function to get column width based on key
  const getColumnWidth = (key: string) => {
    switch (key) {
      case 'id':
        return 'w-[8%]';
      case 'customerName':
        return 'w-[18%]';
      case 'articleNo':
        return 'w-[15%]';
      case 'projectName':
        return 'w-[22%]';
      case 'createdOn':
        return 'w-[14%]';
      case 'status':
        return 'w-[12%]';
      case 'action':
        return 'w-[11%]';
      default:
        return '';
    }
  };

  // For tablet view, we'll show a hybrid - fewer columns than desktop but not cards like mobile
  const renderTableView = () => {
    const columnsToShow = screenSize === 'desktop' ? columns : getPriorityColumns();

    // Function to determine column width
    const getColumnWidth = (column: TableColumn) => {
      // Use column's specified width if available
      if (column.width) return column.width;

      // Check if column.key is defined before calling toLowerCase
      if (!column.key) return 'w-auto';

      switch (column.key.toLowerCase()) {
        case 'id':
        case 'customerid':
          return 'w-[6%]'; // Narrow width for ID
        case 'username':
          return 'w-[15%]'; // Reduced width for username
        case 'loginid':
          return 'w-[22%]'; // Increased width for login ID
        case 'customername':
        case 'customer_name':
        case 'companyname':
        case 'name':
          return 'w-[24%]'; // Wider width for company name
        case 'projectname':
          return 'w-[15%]'; // Fixed width for project name
        case 'duration':
          return 'w-[20%]'; // Fixed width for duration
        case 'status':
          return 'w-[15%]'; // Fixed width for status
        case 'action':
        case 'actions':
          return 'w-[12%]'; // Increased width for actions
        default:
          return ''; // Let other columns take remaining space
      }
    };

    return (
      <div className="w-full">
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-full table-fixed border-collapse">
            <thead className={theadClassName}>
              <tr>
                {columnsToShow.map((column, index) => (
                  <th
                    key={column.key}
                    className={`${thClassName}
                      ${index === 0 ? 'rounded-tl-lg' : ''}
                      ${index === columnsToShow.length - 1 ? 'rounded-tr-lg' : ''}
                      ${getColumnWidth(column)}
                      ${column.minWidth ? `min-w-[${column.minWidth}]` : ''}
                      ${column.maxWidth ? `max-w-[${column.maxWidth}]` : ''}
                      ${column.headerClassName || ''}`}
                  >
                    <div className="flex items-center justify-start space-x-1">
                      {column.icon && <span className="flex-shrink-0">{column.icon}</span>}
                      <span className="truncate">{column.header}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`cursor-pointer ${
                    row.paymentState === 'partial'
                      ? 'bg-amber-50 hover:bg-amber-100'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columnsToShow.map(column => (
                    <td
                      key={column.key}
                      className={`${tdClassName}
                        ${column.truncate !== false ? 'truncate' : 'break-words'}
                        ${column.cellClassName || ''}
                      `}
                      onClick={e => {
                        // Stop propagation if this is an action cell with a renderCell function
                        if (
                          column.renderCell &&
                          (column.key === 'action' || column.key === 'actions')
                        ) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <div className="flex items-center justify-start">
                        {column.key === 'status' ? (
                          <div
                            className={`flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs ${getStatusClass(row[column.key] || row.customerStatus)}`}
                          >
                            <span className="w-4 h-4">
                              {getStatusIcon(row[column.key] || row.customerStatus)}
                            </span>
                            <span>{formatStatus(row[column.key] || row.customerStatus)}</span>
                          </div>
                        ) : column.key === 'createdOn' ? (
                          <div className="flex flex-col items-center">
                            <span className="text-sm">{row[column.key]?.date}</span>
                            <span className="text-xs font-semibold text-gray-500 mt-0.5">
                              {row[column.key]?.time}
                            </span>
                          </div>
                        ) : column.key === 'action' || column.key === 'actions' ? (
                          // Special handling for action columns to ensure they're always visible
                          <div className="flex items-center justify-center space-x-2">
                            {column.renderCell ? column.renderCell(row[column.key], row) : null}
                          </div>
                        ) : column.renderCell ? (
                          column.renderCell(row[column.key], row)
                        ) : (
                          <span className="truncate max-w-full block" title={row[column.key]}>
                            {typeof row[column.key] === 'object' && row[column.key] !== null
                              ? JSON.stringify(row[column.key])
                              : row[column.key]}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Modified card view with updated styling for mobile - Always show action buttons
  const renderCardView = () => {
    const mainColumn =
      columns.find(col => col.key === 'customerName' || col.key === 'companyName') || columns[0];
    const secondaryColumns = columns.filter(
      col =>
        col.key !== 'customerName' &&
        col.key !== 'companyName' &&
        col.key !== 'status' &&
        col.key !== 'action' &&
        col.key !== 'actions'
    );
    // Find action column for edit button
    const actionColumn = columns.find(col => col.key === 'action' || col.key === 'actions');

    return (
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
        {data.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`p-3 rounded-lg border shadow-sm ${
              row.paymentState === 'partial'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-white border-gray-200'
            }`}
            onClick={() => onRowClick && onRowClick(row)}
          >
            <div className="flex flex-col gap-1.5">
              {/* Status at the top */}
              {(row.status || row.customerStatus) && (
                <div
                  className={`inline-flex items-center gap-2 w-fit px-2 py-0.5 rounded-md text-[11px] font-medium ${getStatusClass(row.status || row.customerStatus)}`}
                >
                  <span className="w-3 h-3">{getStatusIcon(row.status || row.customerStatus)}</span>
                  <span>{formatStatus(row.status || row.customerStatus)}</span>
                </div>
              )}

              {/* Title section with icon */}
              <div className="flex items-center gap-2">
                <span className="text-blue-800 flex-shrink-0">
                  {mainColumn.icon || <MdBusinessCenter className="w-4 h-4" />}
                </span>
                <span
                  className="font-semibold text-[13px] text-gray-900"
                  title={row[mainColumn.key]} // Show full text on hover
                >
                  {truncateString(row[mainColumn.key])}
                </span>
              </div>

              {/* Client ID directly below the title - always display this important field */}
              {row.clientId && (
                <div className="flex items-start mt-1">
                  <div className="w-1/3 text-[11px] text-gray-500 flex items-center gap-1">
                    <span className="truncate">Client ID:</span>
                  </div>
                  <div className="w-2/3 text-[12px] font-medium text-gray-900">
                    <span className="truncate">{row.clientId}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
              {secondaryColumns.slice(0, 4).map(column => (
                <div key={column.key} className="flex items-start">
                  <div className="w-1/3 text-[11px] text-gray-500 flex items-center gap-1">
                    {column.icon && <span className="w-3 h-3">{column.icon}</span>}
                    <span className="truncate">{column.header}:</span>
                  </div>
                  <div
                    className="w-2/3 text-[12px] font-medium text-gray-900"
                    title={row[column.key]} // Show full text on hover
                  >
                    {column.key === 'createdOn' ? (
                      <div className="flex flex-col">
                        <span>{row[column.key]?.date}</span>
                        <span className="text-[10px] font-semibold text-gray-500">
                          {row[column.key]?.time}
                        </span>
                      </div>
                    ) : column.key === 'projectName' ? (
                      <span className="text-red-500">
                        {highlightMatch
                          ? highlightMatch(row[column.key], '')
                          : truncateString(row[column.key])}
                      </span>
                    ) : column.renderCell ? (
                      column.renderCell(row[column.key], row)
                    ) : (
                      <span className="truncate">
                        {highlightMatch ? highlightMatch(row[column.key], '') : row[column.key]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between gap-2">
              {/* Use the action column's renderCell function if available */}
              {actionColumn && actionColumn.renderCell ? (
                <div className="flex-1 flex justify-center" onClick={e => e.stopPropagation()}>
                  {actionColumn.renderCell(row[actionColumn.key], row)}
                </div>
              ) : (
                <button
                  className="flex-1 border border-red-500 text-red-500 rounded text-xs px-2 py-1 font-medium flex items-center justify-center gap-1"
                  onClick={e => {
                    e.stopPropagation();
                    // Default edit action if no custom renderCell is provided
                    if (onRowClick) onRowClick(row);
                  }}
                >
                  <FiEdit2 className="w-3 h-3" />
                  Edit
                </button>
              )}
              <button
                className="flex-1 border border-gray-300 text-gray-700 rounded text-xs px-2 py-1 font-medium flex items-center justify-center gap-1"
                onClick={e => {
                  e.stopPropagation();
                  // View more actions
                  if (onRowClick) onRowClick(row);
                }}
              >
                <FiEye className="w-3 h-3" />
                View
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Calculate pagination values with improved error handling
  // Ensure totalItems is at least the length of the data array
  const effectiveTotalItems =
    totalItems !== undefined ? Math.max(totalItems, data.length) : data.length;

  // Use totalPages prop if provided, otherwise calculate it
  const calculatedPages = Math.max(1, Math.ceil(effectiveTotalItems / itemsPerPage));
  const totalPages = providedTotalPages !== undefined ? providedTotalPages : calculatedPages;

  console.log(
    `ResponsiveTable pagination: totalItems=${effectiveTotalItems}, itemsPerPage=${itemsPerPage}, providedTotalPages=${providedTotalPages}, calculatedPages=${calculatedPages}, finalTotalPages=${totalPages}, currentPage=${currentPage}`
  );

  const startIndex = effectiveTotalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIndex = Math.min(startIndex + itemsPerPage - 1, effectiveTotalItems);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pageNumbers.push(1);

    // Calculate middle range
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Add ellipsis if needed
    if (startPage > 2) pageNumbers.push('...');

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Add ellipsis if needed
    if (endPage < totalPages - 1) pageNumbers.push('...');

    // Always show last page
    pageNumbers.push(totalPages);

    return pageNumbers;
  };

  // Fix the pagination buttons rendering
  const renderPagination = () => {
    return getPageNumbers().map((pageNum, idx) => (
      <button
        key={idx}
        onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
        disabled={typeof pageNum !== 'number'}
        className={`w-8 h-8 flex items-center justify-center rounded ${
          pageNum === currentPage
            ? 'bg-red-500 text-white'
            : 'border border-gray-300 hover:bg-gray-50'
        } ${typeof pageNum !== 'number' ? 'cursor-default' : ''}`}
      >
        {pageNum}
      </button>
    ));
  };

  // Add handlers for pagination
  const handlePageChange = (page: number) => {
    onPageChange?.(page);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    setItemsPerPage(newSize); // Update local state
    onItemsPerPageChange?.(newSize); // Notify parent
  };

  if (isLoading) {
    return <TableSkeleton columns={columns.length} rows={defaultItemsPerPage} />;
  }

  return (
    <div className="w-full bg-transparent">
      {title && <h2 className="text-lg font-semibold mb-4">{title}</h2>}

      {data.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-gray-500">{emptyText}</p>
        </div>
      ) : (
        <>
          <div className="hidden lg:block">{renderTableView()}</div>
          <div className="lg:hidden bg-transparent">
            <div>{renderCardView()}</div>
          </div>
        </>
      )}

      {/* Updated Pagination Controls with mobile styling */}
      <div className="mt-6 w-full px-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-transparent">
          {/* Left - Results Count */}
          <div className="text-sm text-gray-600 order-1">
            Showing {startIndex} to {endIndex} of {totalItems} results
          </div>

          {/* Center - Page Numbers */}
          <div className="flex-1 flex justify-center order-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                ≪
              </button>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                ‹
              </button>

              <div className="flex items-center gap-1">{renderPagination()}</div>

              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                ≫
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTable;
